/**
 * The vendor console for the views (PLAN §13 phase 7b item 1, D15, D27): the
 * tenants and their suspension, the ops dashboard, releases and pins, the
 * vendor's activity and the cross-tenant break-glass audit, announcements.
 * The data-hook pattern (`rundowns.ts`).
 *
 * **Live (D35).** Every key sits under a prefix `keysForChange`
 * (`changes.ts`) names: `['vendor','tenants']`, `['vendor','ops']`,
 * `['vendor','releases']`, `['vendor','announcements']`,
 * `['vendor','activity']`. The cloud mirrors each tenant's site, node,
 * production and user changes to the vendor tenant's channel as `tenant`, so
 * the lists refresh themselves; the ops view also re-reads every
 * `OPS_POLL_MS`, because a link going stale is the passing of time, not a
 * write.
 *
 * **Optimistic concurrency.** An announcement save names the version it was
 * made from; a 409 becomes an `AnnouncementConflict` carrying the
 * announcement as it now is (the editor's overwrite-or-discard banner).
 */
import { ApiError, type ApiClient } from '@acp/api-client/client.js';
import type { AuditEntry } from '@acp/api-contract/offlineAuth.js';
import type { ReleasePin } from '@acp/api-contract/releases.js';
import type { CreateTenantRequest } from '@acp/api-contract/tenancy.js';
import type {
  Announcement,
  CreateAnnouncementRequest,
  UpdateAnnouncementRequest,
  VendorActivity,
  VendorOpsResponse,
  VendorReleasesResponse,
  VendorTenant,
  VendorTenantDetail,
} from '@acp/api-contract/vendor.js';
import { infiniteQueryOptions, type QueryClient, queryOptions } from '@tanstack/react-query';

export const vendorKeys = {
  all: ['vendor'] as const,
  tenants: () => [...vendorKeys.all, 'tenants'] as const,
  tenantList: () => [...vendorKeys.tenants(), 'list'] as const,
  tenant: (tenantId: string) => [...vendorKeys.tenants(), 'one', tenantId] as const,
  ops: () => [...vendorKeys.all, 'ops'] as const,
  releases: () => [...vendorKeys.all, 'releases'] as const,
  announcements: () => [...vendorKeys.all, 'announcements'] as const,
  announcementList: () => [...vendorKeys.announcements(), 'list'] as const,
  announcement: (announcementId: string) => [...vendorKeys.announcements(), 'one', announcementId] as const,
  activity: () => [...vendorKeys.all, 'activity'] as const,
  /** The break-glass audit: no change kind names it (a node reports it over the site link); it is re-read on open. */
  audit: () => [...vendorKeys.all, 'audit'] as const,
};

/** How often the ops view is re-read: a node's link goes stale after three missed 30 s heartbeats, with no write to hear. */
export const OPS_POLL_MS = 30_000;

/** The first page's cursor: none. */
const FIRST_PAGE: string | null = null;

/** Entries per page of the activity log and the audit. */
export const VENDOR_PAGE = 50;

// ─── tenants ───

/** Every tenant, by name. */
export function vendorTenantsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: vendorKeys.tenantList(),
    queryFn: async (): Promise<VendorTenant[]> => {
      const res = await api.call('listVendorTenants', { params: null, query: null, body: null });
      return [...res.tenants].sort((a, b) => a.name.localeCompare(b.name) || a.tenantId.localeCompare(b.tenantId));
    },
  });
}

export function vendorTenantQuery(api: ApiClient, tenantId: string) {
  return queryOptions({
    queryKey: vendorKeys.tenant(tenantId),
    queryFn: async (): Promise<VendorTenantDetail> => api.call('getVendorTenant', { params: { tenantId }, query: null, body: null }),
  });
}

async function afterTenantWrite(queryClient: QueryClient): Promise<void> {
  await Promise.all([queryClient.invalidateQueries({ queryKey: vendorKeys.tenants() }), queryClient.invalidateQueries({ queryKey: vendorKeys.ops() }), queryClient.invalidateQueries({ queryKey: vendorKeys.activity() })]);
}

export function createTenantMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (request: CreateTenantRequest): Promise<{ tenantId: string; adminEmail: string }> => {
      const res = await api.call('createTenant', { params: null, query: null, body: request });
      return { tenantId: res.tenant.tenantId, adminEmail: res.adminUser.email };
    },
    onSuccess: async (): Promise<void> => afterTenantWrite(queryClient),
  };
}

export function suspendTenantMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly tenantId: string; readonly reason: string }): Promise<VendorTenant> =>
      (await api.call('suspendTenant', { params: { tenantId: input.tenantId }, query: null, body: { reason: input.reason } })).tenant,
    onSuccess: async (): Promise<void> => afterTenantWrite(queryClient),
  };
}

export function reactivateTenantMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (input: { readonly tenantId: string; readonly note: string }): Promise<VendorTenant> =>
      (await api.call('reactivateTenant', { params: { tenantId: input.tenantId }, query: null, body: { note: input.note } })).tenant,
    onSuccess: async (): Promise<void> => afterTenantWrite(queryClient),
  };
}

// ─── ops ───

export function vendorOpsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: vendorKeys.ops(),
    queryFn: async (): Promise<VendorOpsResponse> => api.call('getVendorOps', { params: null, query: null, body: null }),
    refetchInterval: OPS_POLL_MS,
  });
}

// ─── releases and pins (D15) ───

export function vendorReleasesQuery(api: ApiClient) {
  return queryOptions({
    queryKey: vendorKeys.releases(),
    queryFn: async (): Promise<VendorReleasesResponse> => api.call('getVendorReleases', { params: null, query: null, body: null }),
  });
}

/** Which pin: the stage's, or one site's. */
export type PinTarget = { readonly kind: 'stage' } | { readonly kind: 'site'; readonly siteId: string };

export type PinChange = { readonly target: PinTarget; readonly action: { readonly kind: 'set'; readonly listHash: string; readonly note: string } | { readonly kind: 'clear' } };

export function changePinMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async ({ target, action }: PinChange): Promise<ReleasePin | null> => {
      if (action.kind === 'set') {
        const body = { listHash: action.listHash, note: action.note };
        const res = target.kind === 'stage' ? await api.call('setStagePin', { params: null, query: null, body }) : await api.call('setSitePin', { params: { siteId: target.siteId }, query: null, body });
        return res.pin;
      }
      const res = target.kind === 'stage' ? await api.call('clearStagePin', { params: null, query: null, body: null }) : await api.call('clearSitePin', { params: { siteId: target.siteId }, query: null, body: null });
      return res.pin;
    },
    onSuccess: async (): Promise<void> => {
      await Promise.all([queryClient.invalidateQueries({ queryKey: vendorKeys.releases() }), queryClient.invalidateQueries({ queryKey: vendorKeys.activity() })]);
    },
  };
}

// ─── activity and audit ───

export type ActivityPage = { readonly entries: readonly VendorActivity[]; readonly next: string | null };
export type AuditPage = { readonly entries: readonly AuditEntry[]; readonly next: string | null };

/** The vendor's own actions, newest first, a page at a time. */
export function vendorActivityQuery(api: ApiClient) {
  return infiniteQueryOptions({
    queryKey: vendorKeys.activity(),
    initialPageParam: FIRST_PAGE,
    queryFn: async ({ pageParam }): Promise<ActivityPage> =>
      api.call('listVendorActivity', { params: null, query: pageParam === null ? { limit: VENDOR_PAGE } : { limit: VENDOR_PAGE, before: pageParam }, body: null }),
    getNextPageParam: (last: ActivityPage): string | null => last.next,
  });
}

/** Every tenant's break-glass audit (D20), newest first, a page at a time. */
export function vendorAuditQuery(api: ApiClient) {
  return infiniteQueryOptions({
    queryKey: vendorKeys.audit(),
    initialPageParam: FIRST_PAGE,
    queryFn: async ({ pageParam }): Promise<AuditPage> =>
      api.call('listVendorAudit', { params: null, query: pageParam === null ? { limit: VENDOR_PAGE } : { limit: VENDOR_PAGE, before: pageParam }, body: null }),
    getNextPageParam: (last: AuditPage): string | null => last.next,
  });
}

// ─── announcements ───

/** Every announcement, newest start first. */
export function vendorAnnouncementsQuery(api: ApiClient) {
  return queryOptions({
    queryKey: vendorKeys.announcementList(),
    queryFn: async (): Promise<Announcement[]> => {
      const res = await api.call('listVendorAnnouncements', { params: null, query: null, body: null });
      return [...res.announcements].sort((a, b) => b.startsAt.localeCompare(a.startsAt) || b.announcementId.localeCompare(a.announcementId));
    },
  });
}

export function vendorAnnouncementQuery(api: ApiClient, announcementId: string) {
  return queryOptions({
    queryKey: vendorKeys.announcement(announcementId),
    queryFn: async (): Promise<Announcement> => (await api.call('getVendorAnnouncement', { params: { announcementId }, query: null, body: null })).announcement,
  });
}

async function afterAnnouncementWrite(queryClient: QueryClient, announcement: Announcement | null): Promise<void> {
  if (announcement !== null) queryClient.setQueryData(vendorKeys.announcement(announcement.announcementId), announcement);
  await Promise.all([
    queryClient.invalidateQueries({ queryKey: vendorKeys.announcementList() }),
    queryClient.invalidateQueries({ queryKey: vendorKeys.activity() }),
    // The vendor's own tenant may be one it targets: its banner too.
    queryClient.invalidateQueries({ queryKey: ['announcements'] }),
  ]);
}

export function createAnnouncementMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (request: CreateAnnouncementRequest): Promise<Announcement> => (await api.call('createAnnouncement', { params: null, query: null, body: request })).announcement,
    onSuccess: async (made: Announcement): Promise<void> => afterAnnouncementWrite(queryClient, made),
  };
}

/** A save refused because the announcement moved on: `current` is the announcement as it now is. */
export class AnnouncementConflict extends Error {
  readonly current: Announcement;
  constructor(current: Announcement) {
    super(`conflict: ${current.updatedBy} saved version ${String(current.version)} meanwhile`);
    this.name = 'AnnouncementConflict';
    this.current = current;
  }
}

export type AnnouncementSave = { readonly announcementId: string; readonly edit: UpdateAnnouncementRequest };

export function saveAnnouncementMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async ({ announcementId, edit }: AnnouncementSave): Promise<Announcement> => {
      try {
        return (await api.call('updateAnnouncement', { params: { announcementId }, query: null, body: edit })).announcement;
      } catch (err) {
        if (!(err instanceof ApiError) || err.code !== 'conflict') throw err;
        // Re-read what won, so the editor can offer it.
        throw new AnnouncementConflict((await api.call('getVendorAnnouncement', { params: { announcementId }, query: null, body: null })).announcement);
      }
    },
    onSuccess: async (saved: Announcement): Promise<void> => afterAnnouncementWrite(queryClient, saved),
  };
}

export function deleteAnnouncementMutation(api: ApiClient, queryClient: QueryClient) {
  return {
    mutationFn: async (announcementId: string): Promise<Announcement> => (await api.call('deleteAnnouncement', { params: { announcementId }, query: null, body: null })).announcement,
    onSuccess: async (deleted: Announcement): Promise<void> => {
      queryClient.removeQueries({ queryKey: vendorKeys.announcement(deleted.announcementId) });
      await afterAnnouncementWrite(queryClient, null);
    },
  };
}
